import { ChangeEvent, useCallback, useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { Box } from "@mui/material"
import { RootState } from "../../../../store/store"
import { RUBLE } from "../../../../utils/constants"
import AmountInput from "../../../../components/AmountInput"
import {
  ContentContainer,
  ContinueButton,
  DetailLabel,
  DetailValue,
  MerchantBrandName,
  TopContainer,
  BottomContainer,
  TitleContainer,
  Amount,
} from "../styled"
import { useGetUserInfo } from "../../../../api/hooks/useGetUserInfo"
import {
  clearInputAmount,
  formatCurrency,
  validateAmount,
} from "../../../../components/AmountInput/utils/utils"
import { formatAmount } from "../../../../utils/formatters"

interface StaticPaymentProps {
  onPay: (amount: number) => void
}

const StaticPayment = ({ onPay }: StaticPaymentProps) => {
  const qrcInfo = useSelector((state: RootState) => state.c2bPayment.qrcInfo)
  const [amount, setAmount] = useState(() => qrcInfo?.amount?.toString().replace(".", ",") ?? "")
  const { data: userInfo } = useGetUserInfo()

  const availableBalance = userInfo?.balance.availableAmount || 0
  const availableWithdraw = userInfo?.availableWithdraw || 0
  const buttonDisabled = !amount || validateAmount(amount, availableWithdraw, availableBalance).result

  useEffect(() => {
    if (qrcInfo?.amount) {
      setAmount(qrcInfo.amount.toString().replace(".", ","))
    }
  }, [qrcInfo?.amount])

  const handleAmountChange = useCallback((e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const formattedValue = formatCurrency(e.target.value)
    setAmount(clearInputAmount(formattedValue))
  }, [])

  const handlePay = useCallback(() => {
    if (!qrcInfo?.amount && !amount) return
    onPay(Number(qrcInfo?.amount ?? amount))
  }, [onPay, amount, qrcInfo?.amount])

  return (
    <ContentContainer>
      <TopContainer>
        <MerchantBrandName brandVariant="badge">
          {qrcInfo?.merchantBrandName}
        </MerchantBrandName>
        {qrcInfo?.amount ? (
          <Amount>
            {formatAmount(qrcInfo?.amount)} {RUBLE}
          </Amount>
        ) : (
          <AmountInput
            variant="filled"
            value={amount || "0"}
            label="Сколько"
            onChange={handleAmountChange}
            limit={availableWithdraw}
            balance={availableBalance}
            currency={RUBLE}
            inputProps={{ "data-test-id": "static-payment-amount" }}
          />
        )}
        <DetailLabel>Без комиссии</DetailLabel>
        <Box sx={TitleContainer}>
          <DetailLabel>С баланса:</DetailLabel>
          <DetailValue>
            {formatAmount(availableBalance)} {RUBLE}
          </DetailValue>
        </Box>
        <DetailLabel>Назначение платежа</DetailLabel>
        <DetailValue>{qrcInfo?.description}</DetailValue>
      </TopContainer>
      <BottomContainer>
        <ContinueButton
          variant="contained"
          disabled={buttonDisabled}
          onClick={handlePay}
          data-test-id="static-payment-submit"
        >
          Перевести {amount} {RUBLE}
        </ContinueButton>
      </BottomContainer>
    </ContentContainer>
  )
}

export default StaticPayment
